import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getOrganizerBySlug, getEvents } from '../services/api';
import WhitelabelLayout from '../layouts/WhitelabelLayout';

const OrganizerMiniSite = () => {
    const { slug } = useParams();
    const [organizer, setOrganizer] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            console.log(`🌐 [OrganizerMiniSite] Iniciando carga para el slug: "${slug}"`);
            try {
                setLoading(true);
                // 1. Obtener datos del organizador por slug
                const orgData = await getOrganizerBySlug(slug);
                console.log(`📡 [OrganizerMiniSite] Respuesta de getOrganizerBySlug recibida:`, orgData ? 'Encontrado' : 'No encontrado');
                
                if (!orgData) {
                    setError("El organizador no existe.");
                    setLoading(false);
                    return;
                }
                console.log("🔍 [OrganizerMiniSite] Datos del organizador:", {
                    id: orgData.id,
                    slug: orgData.slug,
                    logoUrl: orgData.logoUrl,
                    bannerUrl: orgData.bannerUrl,
                    brandColor: orgData.brandColor
                });
                setOrganizer(orgData);
                console.log('DEBUG: Organizer data found:', orgData);

                // 2. Obtener eventos de este organizador
                const eventsResponse = await getEvents();
                const eventsList = eventsResponse.events || (Array.isArray(eventsResponse) ? eventsResponse : []);
                const filteredEvents = eventsList.filter(e => e.organizerId === orgData.id);
                setEvents(filteredEvents);

                setLoading(false);
            } catch (err) {
                console.error("Error fetching mini-site data:", err);
                setError("Error al cargar el sitio.");
                setLoading(false);
            }
        };

        fetchData();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-slate-200 border-t-primary-500 rounded-full animate-spin"></div>
                    <p className="text-slate-500 font-medium animate-pulse">Cargando espacio comercial...</p>
                </div>
            </div>
        );
    }

    if (error || !organizer) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center p-8 bg-white rounded-3xl shadow-xl max-w-md mx-4 border border-slate-100">
                    <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
                        ⚠️
                    </div>
                    <h1 className="text-2xl font-bold text-slate-800 mb-2">{error || "No encontrado"}</h1>
                    <p className="text-slate-500 mb-8">Lo sentimos, no hemos podido encontrar el espacio que buscas.</p>
                    <Link to="/" className="inline-block bg-slate-800 text-white px-8 py-3 rounded-xl font-bold hover:bg-slate-700 transition-colors">
                        Ir a Inicio
                    </Link>
                </div>
            </div>
        );
    }

    const brandingColor = organizer.brandColor || '#72c02c';

    return (
        <WhitelabelLayout organizer={organizer}>
            {/* Hero Section */}
            <div className={`relative pt-8 pb-10 overflow-hidden ${organizer.bannerUrl ? 'text-white' : ''}`}>
                {/* Background Decor / Banner */}
                <div className="absolute inset-0 z-0">
                    {organizer.bannerUrl ? (
                        <>
                            <img 
                                src={organizer.bannerUrl} 
                                alt="Banner" 
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px]"></div>
                        </>
                    ) : (
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] bg-gradient-to-b from-[var(--primary-brand)]/5 to-transparent rounded-full blur-3xl opacity-50"></div>
                    )}
                </div>

                <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
                    <div className={`${organizer.bannerUrl ? 'bg-white/10 border-white/20' : 'bg-white/80 border-[var(--primary-brand)]/20'} inline-flex items-center gap-2 backdrop-blur-sm border px-4 py-2 rounded-full mb-8 shadow-sm`}>
                        <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: brandingColor }}></span>
                        <span className={`text-xs font-bold uppercase tracking-widest ${organizer.bannerUrl ? 'text-white/80' : 'text-slate-500'}`}>
                            Espacio del Organizador
                        </span>
                    </div>

                    <h1 className={`text-4xl md:text-5xl font-extrabold mb-4 tracking-tight ${organizer.bannerUrl ? 'text-white' : 'text-slate-900'}`}>
                        {organizer.firstName ? `${organizer.firstName} ${organizer.lastName}` : 'Bienvenidos'}
                    </h1>

                    <p className={`text-lg max-w-2xl mx-auto leading-relaxed mb-8 ${organizer.bannerUrl ? 'text-white/90' : 'text-slate-600'}`}>
                        {organizer.welcomeMessage || 'Únete a nuestros pozos y torneos exclusivos. Gestión profesional para una experiencia de juego superior.'}
                    </p>

                    <div className="flex flex-wrap justify-center gap-4">
                        <a href="#eventos" className="bg-[var(--primary-brand)] text-white px-8 py-3 rounded-2xl font-bold text-base shadow-lg shadow-[var(--primary-brand)]/30 hover:scale-105 transition-all">
                            Ver Pozos Abiertos
                        </a>
                    </div>
                </div>
            </div>

            {/* Eventos Section */}
            <section id="eventos" className="max-w-7xl mx-auto px-4 py-8">
                <div className="mb-8">
                    <div>
                        <h2 className="text-2xl font-extrabold text-slate-900 mb-1">Próximos Eventos</h2>
                        <p className="text-slate-500 text-sm">Inscríbete ahora para asegurar tu plaza</p>
                    </div>
                </div>

                {events.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {events.map(event => (
                            <div key={event.id} className="group bg-white rounded-[32px] overflow-hidden border border-slate-100 shadow-sm hover:shadow-2xl hover:shadow-slate-200/50 transition-all duration-500 hover:-translate-y-2">
                                {/* Decoración superior con el color de marca */}
                                <div className="h-2 w-full" style={{ backgroundColor: brandingColor }}></div>
                                <div className="p-8">
                                    <div className="flex justify-between items-start mb-6">
                                        <div className="bg-slate-50 px-4 py-1.5 rounded-full text-xs font-bold text-slate-500">
                                            {event.date}
                                        </div>
                                        <div className="text-2xl font-black text-slate-900">
                                            {event.price}€
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-bold text-slate-800 mb-4 group-hover:text-primary-brand transition-colors">
                                        {event.title}
                                    </h3>
                                    <div className="space-y-3 mb-10">
                                        <div className="flex items-center gap-3 text-slate-500 text-sm font-medium">
                                            📍 <span className="line-clamp-1">{event.location || 'Club de Pádel'}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-slate-500 text-sm font-medium">
                                            🕒 <span>{event.startTime} - {event.endTime}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-slate-500 text-sm font-medium">
                                            📊 <span>Nivel {event.levelMin} - {event.levelMax}</span>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/o/${organizer.slug}/events/${event.id}`}
                                        className="block w-full text-center py-4 rounded-2xl font-bold text-slate-400 group-hover:bg-[var(--primary-brand)] group-hover:text-white group-hover:shadow-lg group-hover:shadow-[var(--primary-brand)]/20 transition-all border border-slate-100 group-hover:border-[var(--primary-brand)]"
                                    >
                                        Detalles de la Inscripción
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-[40px] border border-dashed border-slate-200 p-20 text-center">
                        <div className="text-6xl mb-6 grayscale text-[var(--primary-brand)] opacity-30">🎾</div>
                        <h3 className="text-xl font-bold text-slate-700 mb-2">No hay eventos a la vista</h3>
                        <p className="text-slate-500 max-w-sm mx-auto">Vuelve pronto para ver los nuevos pozos que estamos preparando.</p>
                    </div>
                )}
            </section>
        </WhitelabelLayout>
    );
};

export default OrganizerMiniSite;
